import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { History, Plus, Pencil, Trash2, User, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getAuthHeader() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const ACTION_CONFIG = {
  created: { label: 'Created', icon: Plus, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  updated: { label: 'Updated', icon: Pencil, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  deleted: { label: 'Deleted', icon: Trash2, color: 'bg-red-50 text-red-700 border-red-200' },
};

const ENTITY_LABELS = {
  equipment: 'Equipment',
  personnel: 'Personnel',
  category: 'Master Data',
};

function formatTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const day = d.getDate().toString().padStart(2, '0');
  const mon = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const mins = d.getMinutes().toString().padStart(2, '0');
  return `${day}/${mon}/${year} ${hours}:${mins}`;
}

function timeAgo(ts) {
  if (!ts) return '';
  const now = new Date();
  const then = new Date(ts);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatTimestamp(ts);
}

function ChangeDetail({ changes }) {
  const entries = Object.entries(changes || {});
  if (entries.length === 0) return null;
  return (
    <div className="mt-2 space-y-1" data-testid="audit-changes">
      {entries.map(([field, vals]) => (
        <div key={field} className="text-xs bg-slate-50 rounded px-2 py-1.5 border border-slate-100">
          <span className="font-medium text-slate-600">{field.replace(/_/g, ' ')}</span>
          <div className="flex items-center gap-1.5 mt-0.5">
            {vals.old !== null && vals.old !== 'None' && (
              <span className="text-red-500 line-through">{vals.old}</span>
            )}
            {vals.old !== null && vals.old !== 'None' && <span className="text-slate-300">&rarr;</span>}
            <span className="text-emerald-600">{vals.new !== 'None' ? vals.new : '(empty)'}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export function AuditTrailPanel({ open, onOpenChange, entityType, entityId, title }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchAuditTrail();
    }
  }, [open, entityType, entityId]);

  const fetchAuditTrail = async () => {
    setLoading(true);
    try {
      let url = `${API}/audit-trail?limit=100`;
      if (entityType) url += `&entity_type=${entityType}`;
      if (entityId) url += `&entity_id=${entityId}`;
      const res = await axios.get(url, { headers: getAuthHeader() });
      setEntries(res.data);
    } catch (error) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="audit-trail-panel">
        <SheetHeader>
          <SheetTitle className="text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <History className="w-5 h-5" />
            {title || 'Activity Log'}
          </SheetTitle>
          <SheetDescription>
            {entityId ? 'Change history for this record' : `Recent changes across ${ENTITY_LABELS[entityType] || 'all'} records`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4" data-testid="audit-trail-entries">
          {loading ? (
            <div className="text-center py-12 text-slate-400">
              <div className="animate-spin w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full mx-auto mb-2" />
              <p className="text-sm">Loading activity...</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <History className="w-10 h-10 mx-auto mb-2 text-slate-200" />
              <p className="text-sm">No activity recorded yet</p>
              <p className="text-xs mt-1">Changes will appear here as they happen</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200" />
              
              <div className="space-y-0">
                {entries.map((entry) => {
                  const config = ACTION_CONFIG[entry.action] || ACTION_CONFIG.updated;
                  const Icon = config.icon;
                  const hasChanges = entry.changes && Object.keys(entry.changes).length > 0;
                  
                  return (
                    <div key={entry.id} className="relative pl-10 py-3 group" data-testid={`audit-entry-${entry.id}`}>
                      {/* Timeline dot */}
                      <div className="absolute left-2.5 top-4 w-3 h-3 rounded-full border-2 border-white bg-slate-300 group-hover:bg-slate-500 transition-colors z-10" />
                      
                      <div className="bg-white rounded-lg border border-slate-100 p-3 hover:border-slate-200 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <Badge className={`text-[10px] px-1.5 py-0 ${config.color}`}>
                              <Icon className="w-2.5 h-2.5 mr-0.5" />
                              {config.label}
                            </Badge>
                            <span className="text-sm font-medium text-slate-900 truncate">{entry.entity_name}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {entry.user_email?.split('@')[0]}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {timeAgo(entry.timestamp)}
                          </span>
                        </div>
                        
                        {hasChanges && <ChangeDetail changes={entry.changes} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function LastModifiedBadge({ createdBy, updatedBy, updatedAt }) {
  const who = updatedBy || createdBy;
  if (!who) return null;
  const shortName = who.split('@')[0];
  return (
    <span className="text-xs text-slate-400 flex items-center gap-1" data-testid="last-modified-badge" title={`${updatedBy ? 'Last modified' : 'Created'} by ${who}${updatedAt ? ' at ' + formatTimestamp(updatedAt) : ''}`}>
      <User className="w-3 h-3" />
      {shortName}
    </span>
  );
}
